// Backup and restore, following the suite standard (/mnt/project-files/suite/backup-standard.md in
// the project, modeled on tjtuesday/poll-worker-management): one .zip with a JSON file per table
// plus manifest.json carrying the standard's top-level fields, a UTC time in the filename, an
// office-wide "last backup by whom" stamp, and the green/yellow/red staleness banner.

import JSZip from "jszip";

// The tool's id in every backup it writes; a file with a different app is refused.
export const BACKUP_APP = "election-task-tracker";
// Bump when the files' shape changes in a way an older tracker couldn't read. Older files must
// always restore (missing pieces get safe defaults); newer ones are refused.
export const SCHEMA_VERSION = 1;
const APP_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
const DAY_MS = 86400000;

export function backupFilename(orgId, takenAt) {
  // UTC, and says so: admins can be in different time zones, and a filename that reads like local
  // time but isn't was a real mix-up in the poll worker app.
  const stamp = takenAt.toISOString().slice(0, 19).replace("T", "-").replace(/:/g, "-");
  const org = String(orgId || "office").replace(/[^a-z0-9-]/gi, "");
  return `${BACKUP_APP}-backup-${org}-${stamp}-UTC.zip`;
}

export async function buildBackupZip({ elections, tasks, people, org, by, takenAt }) {
  const zip = new JSZip();
  zip.file("elections.json", JSON.stringify(elections, null, 2));
  zip.file("tasks.json", JSON.stringify(tasks, null, 2));
  zip.file("people.json", JSON.stringify(people, null, 2));
  zip.file(
    "manifest.json",
    JSON.stringify(
      {
        app: BACKUP_APP,
        schemaVersion: SCHEMA_VERSION,
        appVersion: APP_VERSION,
        savedAt: takenAt.toISOString(),
        savedBy: by || null,
        jurisdiction: org ? { id: org.id, name: org.name } : null,
        counts: { elections: elections.length, tasks: tasks.length, people: people.length },
      },
      null,
      2
    )
  );
  return zip.generateAsync({ type: "blob" });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Reads a backup and says plainly what's wrong with it, so nobody gets as far as "replace all my
// data" with a file that was never going to work.
export async function readBackupZip(file) {
  let zip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    throw new Error("That file isn't a .zip backup. Choose the .zip file you downloaded with \"Download Full Backup\".");
  }
  const readJson = async (name) => {
    const entry = zip.file(name);
    if (!entry) return undefined;
    try {
      return JSON.parse(await entry.async("string"));
    } catch {
      throw new Error(`The backup's ${name} is damaged and can't be read.`);
    }
  };
  const manifest = await readJson("manifest.json");
  if (!manifest || manifest.app !== BACKUP_APP) {
    throw new Error("This doesn't look like an Election Task Tracker backup. A backup from a different tool can't be restored here.");
  }
  const schema = Number(manifest.schemaVersion) || 1;
  if (schema > SCHEMA_VERSION) {
    throw new Error("This backup was made by a newer version of the task tracker. Reload the page and try again; if it still says this, the tracker hasn't been updated yet.");
  }
  // Elections and tasks are the backup; a file without them is damaged, not old. The people list
  // can be missing from an older file: it's rebuilt from the tasks' assignees on load.
  const collections = {};
  for (const name of ["elections", "tasks"]) {
    const list = await readJson(`${name}.json`);
    if (!Array.isArray(list)) throw new Error(`The backup is missing ${name}.json, so it can't be restored. Nothing was changed.`);
    collections[name] = list;
  }
  const people = await readJson("people.json");
  collections.people = Array.isArray(people) ? people : [];
  return { manifest: { ...manifest, savedAt: manifest.savedAt || null, savedBy: manifest.savedBy || null }, collections };
}

// Green within a day of the last backup, yellow from 1 to 3 days, red after 3 days or never.
export function backupBannerLevel(lastBackupAt, lastBackupBy, now = Date.now()) {
  const t = lastBackupAt ? Date.parse(lastBackupAt) : NaN;
  const age = Number.isFinite(t) ? now - t : Infinity;
  const level = age <= DAY_MS ? "green" : age <= 3 * DAY_MS ? "yellow" : "red";
  const label = Number.isFinite(t) ? `Last backup: ${timeAgo(age)}${lastBackupBy ? ` by ${lastBackupBy}` : ""}` : "No backup has ever been taken";
  return { level, label, stale: level !== "green" };
}

export function timeAgo(ms) {
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const d = Math.floor(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
